import { useSuspenseQuery } from '@tanstack/react-query';

import { getServicesQuery } from '@/api/booking/getServicesQuery';
import { shopDetailQuery } from '@/api/shop/shopDetailQuery';
import { useAddAppointment } from '@/hooks/booking/useAddAppointment';
import {
  isCheckoutStarted,
  useOptimisticCartUpdater,
} from '@/hooks/booking/useOptimisticCartUpdater';
import { useSuspenseProfessionalQuery } from '@/hooks/queries/booking/useSuspenseProfessionalQuery';

import type { UseCategoryCartParams, UseCategoryCartResult } from './types';

/**
 * The cart of the category list screens. Reads the same queries as the lists,
 * so it suspends only when they would, and must be called under `Suspense`.
 */
export function useCategoryCart({
  shopRoute,
  cartId,
  promoId,
  barberIdOrRoute,
  isAddedAppointment = false,
}: UseCategoryCartParams): UseCategoryCartResult {
  const { data: shop } = useSuspenseQuery(shopDetailQuery(shopRoute));
  const professional = useSuspenseProfessionalQuery({
    shopId: shop.id,
    barberIdOrRoute,
  });
  const { data: servicesData } = useSuspenseQuery(
    getServicesQuery(shop.id, professional?.barber.id),
  );
  const {
    cart,
    clearServices,
    removeLastCartItem,
    addEmptyCartItem,
    isMutatingCart,
    isFetchingCart,
  } = useOptimisticCartUpdater(shop.id, cartId, servicesData.services, promoId);

  const selectedServicesCount = cart?.items.at(-1)?.services.length ?? 0;
  // A promo or an empty added appointment alone gives the order nothing to show.
  const isOrderShown =
    cart?.items.some((item) => item.services.length > 0) ?? false;

  const clearCurrentAppointment = () => {
    if (isCheckoutStarted()) return;
    if (isAddedAppointment) {
      removeLastCartItem();
      return;
    }
    if (selectedServicesCount > 0) clearServices();
  };

  const addAppointment = useAddAppointment({
    shop,
    cartId: cart?.id,
    barberIdOrRoute: barberIdOrRoute ?? 'any',
    barberId: professional?.barber.id,
    addEmptyCartItem,
  });

  return {
    shop,
    cart: cart ?? null,
    selectedServicesCount,
    isOrderShown,
    isUpdatingCart: isMutatingCart || isFetchingCart,
    clearCurrentAppointment,
    addAppointment,
  };
}
